import { useMutation, useQuery } from "react-query";
import { AxiosError, AxiosResponse } from "axios";
import HttpService from "../../services/http";
import { queryClient } from "../../providers/QueryProvider";
import {
  Lead,
  LeadColumn,
  LeadsSummary,
  CreateLeadInput,
  UpdateLeadInput,
} from "../../types/LeadTypes";

export const leadsKey = (entityId: string) => ["leads", entityId];
const summaryKey = (entityId: string) => ["leads-summary", entityId];
const columnsKey = (entityId: string) => ["leads-columns", entityId];

const base = (customerId: string, entityId: string) =>
  `/${customerId}/${entityId}/lead`;

const invalidateLeadCaches = (entityId: string) => {
  queryClient.invalidateQueries(leadsKey(entityId));
  queryClient.invalidateQueries(summaryKey(entityId));
};

export const useGetLeads = (customerId: string, entityId: string) =>
  useQuery<Lead[], AxiosError<any>>(
    leadsKey(entityId),
    async () => {
      const res = await HttpService.get(`${base(customerId, entityId)}/`);
      return res?.data?.element ?? [];
    },
    { enabled: !!customerId && !!entityId, keepPreviousData: true }
  );

export const useGetLeadsSummary = (customerId: string, entityId: string) =>
  useQuery<LeadsSummary, AxiosError<any>>(
    summaryKey(entityId),
    async () => {
      const res = await HttpService.get(
        `${base(customerId, entityId)}/summary`
      );
      return res?.data?.element ?? { lead_count: 0, lead_limit: 0 };
    },
    { enabled: !!customerId && !!entityId }
  );

export const useGetLeadColumns = (customerId: string, entityId: string) =>
  useQuery<LeadColumn[], AxiosError<any>>(
    columnsKey(entityId),
    async () => {
      const res = await HttpService.get(
        `${base(customerId, entityId)}/columns`
      );
      return res?.data?.element ?? [];
    },
    { enabled: !!customerId && !!entityId }
  );

export const useSaveLeadColumns = (customerId: string, entityId: string) =>
  useMutation<AxiosResponse, AxiosError<any>, LeadColumn[]>(
    (columns) =>
      HttpService.put(`${base(customerId, entityId)}/columns`, { columns }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(columnsKey(entityId));
        invalidateLeadCaches(entityId);
      },
    }
  );

export const useCreateLead = (customerId: string, entityId: string) =>
  useMutation<AxiosResponse, AxiosError<any>, CreateLeadInput>(
    (input) => HttpService.post(`${base(customerId, entityId)}/`, input),
    { onSuccess: () => invalidateLeadCaches(entityId) }
  );

export const useUpdateLead = (customerId: string, entityId: string) =>
  useMutation<
    AxiosResponse,
    AxiosError<any>,
    { leadId: string; patch: UpdateLeadInput }
  >(
    ({ leadId, patch }) =>
      HttpService.patch(`${base(customerId, entityId)}/${leadId}`, patch),
    { onSuccess: () => invalidateLeadCaches(entityId) }
  );

export const useDeleteLead = (customerId: string, entityId: string) =>
  useMutation<AxiosResponse, AxiosError<any>, string>(
    (leadId) => HttpService.delete(`${base(customerId, entityId)}/${leadId}`),
    { onSuccess: () => invalidateLeadCaches(entityId) }
  );

export const useConvertLead = (customerId: string, entityId: string) =>
  useMutation<
    AxiosResponse,
    AxiosError<any>,
    { leadId: string; email?: string }
  >(
    ({ leadId, email }) =>
      HttpService.post(
        `${base(customerId, entityId)}/${leadId}/convert`,
        email ? { email } : {}
      ),
    {
      onSuccess: () => {
        invalidateLeadCaches(entityId);
        queryClient.invalidateQueries(["entity-users"], {
          exact: false,
          refetchActive: true,
        });
        queryClient.invalidateQueries(["searchUsers"]);
      },
    }
  );
